/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CreateMerchantRequest } from '../models/CreateMerchantRequest';
import type { MerchantDto } from '../models/MerchantDto';
import type { MerchantItemSupplyHistoryResponse } from '../models/MerchantItemSupplyHistoryResponse';
import type { MerchantProjection } from '../models/MerchantProjection';
import type { Page_MerchantDto_ } from '../models/Page_MerchantDto_';
import type { Page_MerchantItemProjection_ } from '../models/Page_MerchantItemProjection_';
import type { UpdateMerchantRequest } from '../models/UpdateMerchantRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class MerchantControllerService {
  /**
   * getAllMerchants
   * @returns Page_MerchantDto_ OK
   * @throws ApiError
   */
  public static getAllMerchantsUsingGet({
    limit = 10,
    offset,
    search,
  }: {
    /**
     * limit
     */
    limit?: number;
    /**
     * offset
     */
    offset?: number;
    /**
     * search
     */
    search?: string;
  }): CancelablePromise<Page_MerchantDto_> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/merchants',
      query: {
        limit: limit,
        offset: offset,
        search: search,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * createMerchant
   * @returns MerchantDto OK
   * @returns any Created
   * @throws ApiError
   */
  public static createMerchantUsingPost({
    request,
  }: {
    /**
     * request
     */
    request: CreateMerchantRequest;
  }): CancelablePromise<MerchantDto | any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/merchants',
      body: request,
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * updateMerchant
   * @returns MerchantDto OK
   * @returns any Created
   * @throws ApiError
   */
  public static updateMerchantUsingPut({
    request,
  }: {
    /**
     * request
     */
    request: UpdateMerchantRequest;
  }): CancelablePromise<MerchantDto | any> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/merchants',
      body: request,
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getAllMerchantsUnpaged
   * @returns MerchantProjection OK
   * @throws ApiError
   */
  public static getAllMerchantsUnpagedUsingGet(): CancelablePromise<Array<MerchantProjection>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/merchants/unpaged',
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getMerchant
   * @returns MerchantDto OK
   * @throws ApiError
   */
  public static getMerchantUsingGet({
    merchantId,
  }: {
    /**
     * merchantId
     */
    merchantId: string;
  }): CancelablePromise<MerchantDto> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/merchants/{merchantId}',
      path: {
        merchantId: merchantId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * deleteMerchant
   * @returns any OK
   * @throws ApiError
   */
  public static deleteMerchantUsingDelete({
    merchantId,
  }: {
    /**
     * merchantId
     */
    merchantId: string;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/merchants/{merchantId}',
      path: {
        merchantId: merchantId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
      },
    });
  }

  /**
   * deleteMerchantItem
   * @returns any OK
   * @throws ApiError
   */
  public static deleteMerchantItemUsingDelete({
    itemId,
    merchantId,
  }: {
    /**
     * itemId
     */
    itemId: string;
    /**
     * merchantId
     */
    merchantId: string;
  }): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/merchants/{merchantId}/item/{itemId}',
      path: {
        itemId: itemId,
        merchantId: merchantId,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
      },
    });
  }

  /**
   * getMerchantItemSupplyHistory
   * @returns MerchantItemSupplyHistoryResponse OK
   * @throws ApiError
   */
  public static getMerchantItemSupplyHistoryUsingGet({
    itemId,
    merchantId,
    endDate,
    limit = 10,
    offset,
    search,
    startDate,
  }: {
    /**
     * itemId
     */
    itemId: string;
    /**
     * merchantId
     */
    merchantId: string;
    /**
     * endDate
     */
    endDate?: string;
    /**
     * limit
     */
    limit?: number;
    /**
     * offset
     */
    offset?: number;
    /**
     * search
     */
    search?: string;
    /**
     * startDate
     */
    startDate?: string;
  }): CancelablePromise<MerchantItemSupplyHistoryResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/merchants/{merchantId}/item/{itemId}/supply-history',
      path: {
        itemId: itemId,
        merchantId: merchantId,
      },
      query: {
        endDate: endDate,
        limit: limit,
        offset: offset,
        search: search,
        startDate: startDate,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }

  /**
   * getMerchantItems
   * @returns Page_MerchantItemProjection_ OK
   * @throws ApiError
   */
  public static getMerchantItemsUsingGet({
    merchantId,
    limit = 10,
    offset,
    search,
  }: {
    /**
     * merchantId
     */
    merchantId: string;
    /**
     * limit
     */
    limit?: number;
    /**
     * offset
     */
    offset?: number;
    /**
     * search
     */
    search?: string;
  }): CancelablePromise<Page_MerchantItemProjection_> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/merchants/{merchantId}/items',
      path: {
        merchantId: merchantId,
      },
      query: {
        limit: limit,
        offset: offset,
        search: search,
      },
      errors: {
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
      },
    });
  }
}
